/**
 * Loyalty ledger for one customer -- phase 6b, Task 4.
 *
 * `GET /admin/discounts/customers/:id/ledger` -- paginated `loyalty_ledger`
 * rows, newest first (server-ordered; this table renders in response
 * order). Empty (not 404) for a customer who exists but never had a loyalty
 * card -- rendered as a plain empty state, no error.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { discountsApi, qk } from '../api';
import { toApiFailure } from '../errors';
import type { LedgerReason } from '../types';
import { useScrollToListTop } from '../../../hooks/useScrollToListTop';

const REASON_COLOR: Record<LedgerReason, 'success' | 'default' | 'info' | 'warning' | 'error'> = {
  EARN: 'success',
  OPENING_BALANCE: 'default',
  REDEEM: 'info',
  REVERSAL: 'warning',
  ADJUSTMENT: 'error', // goodwill/manual rows should stand out for the audit trail
};

const formatDateTime = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-ZA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const DEFAULT_LIMIT = 25;

export const LedgerTable: React.FC<{ customerId: string }> = ({ customerId }) => {
  const [page, setPage] = useState(0); // MUI TablePagination is 0-based; the API's `page` is 1-based
  const listTopRef = useScrollToListTop(page);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);

  const { data, isLoading, error } = useQuery({
    queryKey: qk.ledger(customerId, { page: page + 1, limit }),
    queryFn: () => discountsApi.customerLedger(customerId, { page: page + 1, limit }),
  });

  return (
    <Box ref={listTopRef}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        Points ledger
      </Typography>

      {isLoading && <Skeleton height={200} />}

      {error && <Alert severity="error">{toApiFailure(error).message}</Alert>}

      {data && data.data.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No ledger activity yet.
        </Typography>
      )}

      {data && data.data.length > 0 && (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Reason</TableCell>
                <TableCell align="right">Points</TableCell>
                <TableCell align="right">Consumed</TableCell>
                <TableCell align="right">Shortfall</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell>Order</TableCell>
                <TableCell>Note</TableCell>
                <TableCell>Created by</TableCell>
                <TableCell>Created at</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.data.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Chip size="small" label={row.reason} color={REASON_COLOR[row.reason]} variant="outlined" />
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: row.points >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}
                  >
                    {row.points >= 0 ? `+${row.points}` : row.points}
                  </TableCell>
                  <TableCell align="right">{row.pointsConsumed}</TableCell>
                  <TableCell align="right">{row.shortfall}</TableCell>
                  <TableCell>{formatDateTime(row.expiresAt)}</TableCell>
                  <TableCell>{row.orderId ?? '—'}</TableCell>
                  <TableCell>{row.note ?? '—'}</TableCell>
                  <TableCell>{row.createdBy ?? '—'}</TableCell>
                  <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={data.total}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={limit}
            onRowsPerPageChange={(e) => {
              setLimit(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </>
      )}
    </Box>
  );
};

export default LedgerTable;
